import { EventEmitter } from 'events';
import { IInboundSignalMessage, IFluidDataStoreContext } from '@fluidframework/runtime-definitions';
import { IFluidDataStoreRuntime } from '@fluidframework/datastore-definitions';
import { IContainerRuntime } from '@fluidframework/container-runtime-definitions';
import { ISequencedDocumentMessage } from '@fluidframework/protocol-definitions';
import { AttachState, ReadOnlyInfo } from '@fluidframework/container-definitions';
import {
  isMfsDeleteSignalData,
  isMfsFindClientResponse,
  isMfsGetSessionDataRequest,
  isMfsGetSessionDataResponse,
  isMfsSetSignalData,
  isMfsSignalPayload,
  isMfsSignalType,
  MfsDefinedValue,
} from '../../volatileSession';
import { assertPrecondition, mfsError } from '../../errors';
import {
  MfsDeleteSignalData,
  MfsFindClientsResponseData,
  MfsPendingSignalToReceive,
  MfsPendingSignalToSend,
  MfsSessionDataRequest,
  MfsSessionDataResponse,
  MfsSetSignalData,
  MfsSignalPayload,
  MfsSignalType,
  SessionDataMapType,
} from './signalType';
import { MfsHost } from '../../store/mfsHost';
import { GenericEventEmitter } from '../../store/mfsEventTypes';
import { logVerbose } from '../../log';

/** Signal events being sent */
export type MfsSignalEvents = {
  signalReceived: {
    signalType: MfsSignalType;
    data: MfsDefinedValue;
    referenceSequenceNumber: number;
  };
};

/** Interface for the signal manager */
export interface MfsSignalManagerInterface extends GenericEventEmitter<MfsSignalEvents> {
  // Broadcast a signal about data being set.
  sendSetData(data: MfsSetSignalData): void;

  // Broadcast a signal about data being deleted.
  sendDeleteData(data: MfsDeleteSignalData): void;

  // Broadcast a signal to get session data from a buddy client
  sendSessionDataRequest(targetClientId: string): void;

  // Broadcast a signal to provide session data
  sendSessionDataResponse(sessionDataMap: SessionDataMapType): void;
}

/** Handles incoming and outgoing signals */
export class MfsSignalManager extends EventEmitter implements MfsSignalManagerInterface {
  /** Store current client's client id. */
  private selfClientId?: string;

  // We only send signals when there is no pending local ops, because we need the sequence number to indicate when the data was made.
  // If there are any pending local ops, the signal will not be sent out until all pending local ops are confirmed.
  // An empty array means no pending signals.

  /** Pending signals which will be sent once local ops are processed */
  private pendingSignalsToSend: MfsPendingSignalToSend[];

  // We cannot guarantee the order of signals and ops. So the signal may be received before/after the ops that the signal was relative to.
  // If the signal is received after the related op, we can directly display it; otherwise, we store it to this variable and process it when the related op comes.

  /** Pending signals to be received which will be processed once local ops are processed */
  private pendingReceivedSignals: MfsPendingSignalToReceive[];
  /** True, if you are a newly joined client and need to find another client to get the existing session data from */
  private lookingForOtherClients: boolean;
  /** True, if you are a newly joined client and need to be caught up on the existing session data */
  private lookingForData: boolean;

  constructor(
    /** Used to silo signals on a per-app level */
    private readonly appId: MfsHost['id'],
    private readonly runtime: IFluidDataStoreRuntime,
    private readonly context: IFluidDataStoreContext
  ) {
    super();
    this.appId = appId;
    this.pendingSignalsToSend = [];
    this.pendingReceivedSignals = [];
    this.lookingForOtherClients = true;
    this.lookingForData = true;

    this.selfClientId = this.runtime.clientId;
    this.listenToSignal();
    this.listenToSavedDocumentEvent();
    this.listenToOp();
    if (this.isConnectedAndAttached()) {
      this.findClientsRequest();
    }
    this.runtime.on('connected', this.findClientsRequest.bind(this));
    this.runtime.on('attached', this.findClientsRequest.bind(this));
  }

  /** Runtime is connected and attached */
  private isConnectedAndAttached(): boolean {
    return this.runtime.connected && this.runtime.attachState === AttachState.Attached;
  }

  /** Can send signal, as long as container is not read-only */
  private sendSignalIfNotReadOnly(readOnlyInfo?: ReadOnlyInfo): boolean {
    if (readOnlyInfo) {
      return !readOnlyInfo.readonly || !readOnlyInfo.storageOnly;
    }
    return true;
  }

  /** Signal sent when session data is set */
  public sendSetData(data: MfsSetSignalData): void {
    this.submitData(data, 'setDataSignal');
  }

  /** Signal sent when session data is deleted */
  public sendDeleteData(data: MfsDeleteSignalData): void {
    this.submitData(data, 'deleteDataSignal');
  }

  /** Signal sent to find other clients on the pod of the same app */
  private findClientsRequest(): void {
    if (this.lookingForOtherClients) {
      this.submitData(null, 'findClientsRequest');
    }
  }

  /** Reponse signal from clients on the pod of the same app */
  private findClientsResponse(): void {
    // if the client ID is defined, respond
    if (!this.selfClientId) {
      this.selfClientId = this.runtime.clientId;
    }
    if (this.selfClientId) {
      const findClientsResponse: MfsFindClientsResponseData = { responderClientId: this.selfClientId };
      this.submitData(findClientsResponse, 'findClientsResponse');
    }
  }

  /** Signal sent to get session data from buddy client */
  public sendSessionDataRequest(targetClientId: string): void {
    const sessionDataRequest: MfsSessionDataRequest = { targetClientId };
    this.submitData(sessionDataRequest, 'getSessionDataRequest');
  }

  /** Signal sent by buddy client containing session data map */
  public sendSessionDataResponse(sessionDataMap: SessionDataMapType): void {
    const serializedDataSets = Array.from(sessionDataMap.values());
    const nestedSessionData = serializedDataSets.map((innerMap) => Array.from(innerMap.values()));
    // an array of all the session data objects
    const sessionData = nestedSessionData.reduce((accumulator, val) => accumulator.concat(val), []);
    const mfsSessionDataResponse: MfsSessionDataResponse = { internalSessionDataArray: sessionData };
    this.submitData(mfsSessionDataResponse, 'getSessionDataResponse');
  }

  /** Process data for signals to be sent */
  private submitData(data: MfsDefinedValue, signalType: MfsSignalType): void {
    const payload: MfsSignalPayload = {
      senderAppId: this.appId,
      data: data,
      refSeqNumber: this.runtime.deltaManager.lastSequenceNumber,
    };
    logVerbose('submitData', 'submitting signal', {
      senderAppId: this.appId,
      signalType,
      refSeqNumber: payload.refSeqNumber,
    });
    if (
      // runtime is connected and attached
      this.isConnectedAndAttached() &&
      // document is not dirty, i.e. there are no pending local changes
      !(this.context.containerRuntime as IContainerRuntime).isDirty &&
      // container is not read-only
      this.sendSignalIfNotReadOnly(this.runtime.deltaManager.readOnlyInfo)
    ) {
      // Only send signal when there is no pending local ops, because we need the sequence number to indicate when the data was made.
      // Each client have a sequence number which is keeping track of the operation that was made
      // So we need to include this sequence number in signal's payload
      logVerbose('submitData', `Signal submitted`, {
        senderAppId: this.appId,
        signalType,
        refSeqNumber: payload.refSeqNumber,
      });
      this.runtime.submitSignal(signalType, payload);
    } else {
      logVerbose('submitData', `Signal submission is pending; signalType:${signalType}, appId: ${payload.senderAppId}`);
      this.pendingSignalsToSend.push({ signalType: signalType, data: data });
    }
  }

  /** Listen to incoming signals and process them */
  private listenToSignal() {
    this.runtime.on('signal', (message: IInboundSignalMessage, local: boolean) => {
      logVerbose('listenToSignal', 'Received signal', { type: message.type, local });
      // For setData we want clients to process their own messages.
      // When a client calls setData or deleteData, their in-memory map is updated
      // However the sequence number they use might be an older sequence number compared to the one in the signal they send
      // The client processes its own message to ensure that the sequence number in their in-memory map is up to date.
      if (local && message.type !== 'setDataSignal' && message.type !== 'deleteDataSignal') return;
      if (!message.content || typeof message.content.refSeqNumber !== 'number') return;

      // only want to process signals of your OWN app
      if (message.content.senderAppId !== this.appId) return;
      // assert that message type is SignalType and content type is MfsSignalPayload
      const payload = message.content;
      assertPrecondition(isMfsSignalPayload(payload));
      assertPrecondition(isMfsSignalType(message.type));
      if (payload.refSeqNumber <= this.runtime.deltaManager.lastSequenceNumber) {
        // if you receive a find clients request or response, handle that separately
        if (
          message.type === 'findClientsRequest' ||
          message.type === 'findClientsResponse' ||
          message.type === 'getSessionDataRequest' ||
          message.type === 'getSessionDataResponse'
        ) {
          return this.listenToCatchupSignals(message.type, payload);
        }
        this.emit('signalReceived', message.type, payload.data, payload.refSeqNumber);
      } else {
        logVerbose(
          'listenToSignal',
          `Signal reception is pending; signalType:${message.type}, seqNum: ${payload.refSeqNumber}, appId: ${payload.senderAppId}`
        );
        // if the sequence number signal referred to is larger, we cannot guarantee to display it at the correct position until the ops received.
        this.pendingReceivedSignals.push({ signalType: message.type, payload });
      }
    });
  }

  /** Listen to signals relating to catchup */
  private listenToCatchupSignals(signalType: MfsSignalType, payload: MfsSignalPayload) {
    logVerbose('listenToCatchupSignals', 'listening catchup signals', { signalType });
    if (signalType === 'findClientsRequest') {
      this.findClientsResponse();
    }
    // if you receive a find clients response, turn off looking for buddy state and send buddy a new request asking for data
    if (signalType === 'findClientsResponse') {
      this.lookingForOtherClients = false;
      assertPrecondition(isMfsFindClientResponse(payload.data));
      if (this.lookingForData) {
        this.sendSessionDataRequest(payload.data.responderClientId);
      }
    }

    if (signalType === 'getSessionDataRequest') {
      assertPrecondition(isMfsGetSessionDataRequest(payload.data));
      // only process the getSessionDataRequest signal if you are the target client
      if (payload.data.targetClientId === this.selfClientId) {
        this.emit('signalReceived', signalType, payload.data, payload.refSeqNumber);
      }
    }

    if (signalType === 'getSessionDataResponse') {
      // this is false if you are already caught up on the existing session data, in which case you don't need to process this signal
      if (!this.lookingForData) {
        return;
      }
      // mark false to indicate you are now caught up on the existing session data
      this.lookingForData = false;
      this.emit('signalReceived', signalType, payload.data, payload.refSeqNumber);
    }
  }

  /** Ensure that there are no pending ops */
  private listenToSavedDocumentEvent() {
    // When there is no pending ops, we can send the pending signal
    const containerRuntime = this.context.containerRuntime as IContainerRuntime;

    containerRuntime.on('saved', () => {
      this.processPendingSignalsToSend();
    });
  }

  /** Try to send signal again */
  private resubmitSignal(signalType: MfsSignalType, data: MfsDefinedValue): void {
    logVerbose('resubmitSignal', 'Trying to send signal again', { signalType });
    if (signalType == 'setDataSignal') {
      assertPrecondition(isMfsSetSignalData(data));
      return this.sendSetData(data);
    }
    if (signalType == 'deleteDataSignal') {
      assertPrecondition(isMfsDeleteSignalData(data));
      return this.sendDeleteData(data);
    }
    if (signalType === 'findClientsRequest') {
      // don't need to assert precondition here, because we don't use the data
      return this.findClientsRequest();
    }

    if (signalType === 'findClientsResponse') {
      assertPrecondition(isMfsFindClientResponse(data));
      return this.submitData(data, 'findClientsResponse');
    }

    if (signalType === 'getSessionDataRequest') {
      assertPrecondition(isMfsGetSessionDataRequest(data));
      return this.submitData(data, 'getSessionDataRequest');
    }

    if (signalType === 'getSessionDataResponse') {
      assertPrecondition(isMfsGetSessionDataResponse(data));
      return this.submitData(data, 'getSessionDataResponse');
    }
  }

  /** Send pending signals */
  private processPendingSignalsToSend(): void {
    const startingLength = this.pendingSignalsToSend.length;
    logVerbose('processPendingSignalsToSend', 'processing pending singals', { startingLength });
    for (let index = startingLength - 1; index >= 0; index -= 1) {
      const pendingSignalToSend = this.pendingSignalsToSend[index];
      this.resubmitSignal(pendingSignalToSend.signalType, pendingSignalToSend.data);
    }
    this.pendingSignalsToSend.splice(0, startingLength);
  }

  /** Listen to incoming ops and try to send and receive pending signals */
  private listenToOp(): void {
    this.runtime.deltaManager.inbound.on('op', (_op: ISequencedDocumentMessage) => {
      // For the pending signals to send out, resubmit it if document is clean
      if (!(this.context.containerRuntime as IContainerRuntime).isDirty) {
        this.processPendingSignalsToSend();
      }
      // For the received signals that is waiting for this op, we can process it now.
      for (let index = this.pendingReceivedSignals.length - 1; index >= 0; index -= 1) {
        const pendingReceivedSignal = this.pendingReceivedSignals[index];
        if (pendingReceivedSignal.payload.refSeqNumber <= this.runtime.deltaManager.lastSequenceNumber) {
          this.emit(
            'signalReceived',
            pendingReceivedSignal.signalType,
            pendingReceivedSignal.payload.data,
            pendingReceivedSignal.payload.refSeqNumber
          );
          this.pendingReceivedSignals.splice(index, 1);
        }
      }
    });
  }
}
